/** @jsx jsx */
import { jsx } from 'theme-ui';
import { graphql } from 'gatsby';
import { BuilderComponent, BuilderContent, Builder, builder } from '@builder.io/react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import * as queryString from 'query-string';

import '../builder-settings.js';

import Layout from '../components/layout/index.jsx';
import { setupServersideConfig } from '../utilities/customSetupServerside.js';
import { setupContentLoadedDataLayer, setupCookiesClientside } from '../utilities/customSetupClientside.js';
import Header from '../components/header/header.jsx';
import Footer from '../components/footer/footer.jsx';

/*
~ Important Gotchas ~
* This file is executed *both* in the Serverside Rendering (SSR) and Clientside on page load.
* If there is an error in the logic in this file while a user tries to access a marketingPage, the NotFoundPage and status_code=404 may be served instead, despite the correct status_code being 500
* The only reliable way for clientside logic to access an A/B-test-page's variant data is via the clientside contentLoaded hook.
** The pageData from graphql has data on variants, but it can't be used clientside for (e.g.) "Variant B was served to the user; push X into the dataLayer"
** Therefore, you should not implement variant logic here. Either do it in the Builder GUI, or have logic in GTM that reads and acts on BLDR_VARIANT_NAME
* DataLayer variables set: ["BLDR_PAGE_NAME", "BLDR_PAGE_ID", "BLDR_VARIANT_NAME", "BLDR_VARIANT_ID", "BLDR_VARIANT_TEST_RATIO", "country", "LS_VARIANT", "omCampaignId"] plus the k:v {"event": "enableOM"}
*/


const HavenPageTemplate = ({ data, location }) => {
  const [loaded, setLoaded] = useState(false);                        // A React Hook that keeps track if the contentLoaded hook has been run, preventing a bug specific to our setup where the contentLoaded Hook can/will run twice
  var { pageData, regionConfig } = setupServersideConfig( data, location );
  var favicon, faviconUrl;
  const urlParams = location ? queryString.parse(location.search) : {}; 

  // Adding top margin to the main content to account for the floating nav
  var navMargin = '75px';

  var faviconUrl = 'https://cdn.builder.io/api/v1/image/assets%2Ff9dd6bab38b6461a948dbc919c8394de%2Ff5c03e2895274420b2ab38226d8167ff';
  favicon = <link rel="shortcut icon" type="image/x-icon" href={faviconUrl} />;

  return (
    <Layout                                                            // Dynamically generate Header, Footer, and GTM Noscript IFrame based on country and language of page
      backgroundColor="white"
      regionConfig={regionConfig}
    >
      <Helmet>
        <title>{regionConfig.defaultTitle}</title>
        {favicon}
        <meta
          name="description"
          content={(pageData && pageData?.data?.description) || regionConfig.defaultDescription}
        />
        <script type="text/javascript" innerHTML={regionConfig.headGTMScript} />
      </Helmet>

      <BuilderContent content={pageData} model="page">
        { (variation, loading, content ) => {
          var variantData,
              pageProperties = variation,
              hideNav = variation?.hideEntireNav;

          // Get the page URL - either from target page or current content
          var pageUrl = content?.data?.url || pageData?.data?.url;
          
          // If target page custom field is set - replace page content with target page's content.
          if(variation && variation.targetPage && variation.targetPage.value) {
            var variationName = variation.targetPage.value.name;
            
            pageData = variation.targetPage.value;
            pageUrl = variation.targetPage.value.data.url; // Update pageUrl to target page URL

            variantData = {
              pageName: content.name,
              name: variationName,
              path: variation.targetPage.value.data.url,
              id: content.testVariationId,
              testRatio: content.testRatio
            };   

            // Use the page properties of the variation's target page
            pageProperties = pageData.data;
            hideNav = pageProperties?.hideEntireNav;
          } 

          // Remove "/kelly/" prefix from pageUrl if it exists
          if (pageUrl && pageUrl.startsWith('/kelly/')) {
            pageUrl = pageUrl.replace(/^\/kelly\//, '');
          }

          // Store page URL globally so components can access it
          if (typeof window !== 'undefined' && pageUrl) {
            window.__builderPageUrl = pageUrl;
          }

          return (
            <div>
              {!hideNav && regionConfig?.region && <Header regionConfig={regionConfig} pageData={pageProperties} />}
              {regionConfig?.bodyGTMIframe &&
                <noscript>
                  {regionConfig.bodyGTMIframe}
                </noscript>
              }
              {/* <div style={{height:navMargin}}></div> */}
            <BuilderComponent
              model={regionConfig.modelName}
              content={pageData}                                              // this line ensures all non-Header/Footer content is ServerSide-Rendered
              contentLoaded={(_, content) => {                                // this React Hook executes clientside only. It runs twice, undesirably
    
                if (!Builder.isEditing && !loaded) {                         // This prevents GTM from loading and populating e.g. OptinMonster popups for the builder.io/content UI. Also ensure it only runs once.
                  setLoaded(true);
                  setupContentLoadedDataLayer( pageData, regionConfig, content, variantData );
                  setupCookiesClientside(regionConfig, location, pageData);
                }
              }}
            />
            {regionConfig?.region && <Footer regionConfig={regionConfig} />}
            </div>
          )}
       }     
      </BuilderContent>
    </Layout>
  );
};

export default HavenPageTemplate;

/* Notes on this:
* This is run before the above (i.e. first) in the SSR
* This is NOT string interpolation. This is the Gatsby GraphQL tag function
*/
export const pageQuery = graphql`
  query($path: String!) {
    allBuilderModels {
      oneKelly(
        target: { urlPath: $path }
        options: { cachebust: true }
      ) {
        content
      },
      oneKellyTest(
        target: { urlPath: $path }
        options: { cachebust: true, includeRefs: true }
      ) {
        content
      },
    }
}
`;